import { userHasDomainAdminRole } from './user-roles';

export const ADMINISTRATION_DISABLED_SETTING_KEY = 'administration_disabled';
export const MULTITENANT_ENVIRONMENT_SETTING_KEY = 'multitenant_environment';
export const ADMIN_FEATURE_DISABLED_KEY = 'admin_feature_disabled';
export const ADMIN_FEATURE_NOT_AVAILABLE_MULTITENANT_KEY = 'admin_feature_not_available_multitenant';
export const ADMIN_FEATURE_REQUIRES_DOMAIN_ADMIN_KEY = 'admin_feature_requires_domain_admin';

export const ADMINISTRATION_FEATURES = ['agents', 'connectors', 'forms', 'dashboards'] as const;

export type AdministrationFeature = (typeof ADMINISTRATION_FEATURES)[number];

export interface AdministrationPolicy {
  disabledFeatures: readonly AdministrationFeature[];
  multitenant: boolean;
  /** The caller has `domainadmin`, the cross-tenant platform administrator role. */
  platformAuthority?: boolean;
}

export type AdministrationUnavailableReason = 'disabled' | 'multitenant' | 'platform';

type RouteLike = { name?: unknown; path?: string } | null | undefined;

// App pages of each feature. Matched on whole path segments anywhere in the
// path, so a base path in front of `/admin` does not matter.
const FEATURE_APP_PATHS: Record<AdministrationFeature, string[]> = {
  agents: ['admin/agents'],
  connectors: ['admin/connector'],
  forms: ['admin/forms'],
  dashboards: ['admin/dashboards'],
};

// Hub API endpoints of each feature, as the API classifies them.
const FEATURE_API_PREFIXES: Record<AdministrationFeature, string[]> = {
  agents: ['/admin/agents', '/agent', '/my/agent'],
  connectors: ['/admin/connector', '/my/connector'],
  forms: ['/admin/forms', '/my/forms'],
  dashboards: ['/admin/dashboards', '/my/dashboards'],
};

const FEATURE_API_EXACT_PATHS: Partial<Record<AdministrationFeature, string[]>> = {
  forms: ['/my/resources/forms'],
  dashboards: ['/my/resources/dashboards'],
};

// Administration that does not exist in multitenant mode. `/admin/dashboard`,
// the overview, is a different page.
const MULTITENANT_REMOVED_PATHS = ['admin/guides', 'admin/forms', 'admin/dashboards'];

const FEATURE_ROUTE_NAMES: Record<AdministrationFeature, (name: string) => boolean> = {
  agents: (name) => name === 'admin-agents',
  connectors: (name) => name.startsWith('admin-connector-'),
  forms: (name) => name === 'admin-forms',
  dashboards: (name) => name === 'admin-dashboards',
};

const MULTITENANT_REMOVED_ROUTE_NAMES = ['admin-guides', 'admin-forms', 'admin-dashboards'];

// Administration that only a platform administrator (`domainadmin`) uses in
// multitenant mode. Tenant administrators keep users, items, options,
// shortcuts, bulletins and the overview.
const PLATFORM_ONLY_APP_PATHS = [
  'admin/roles',
  'admin/workspaces',
  'admin/locales',
  'admin/domains',
  'admin/resources',
  'admin/system-logs',
  'admin/logs',
  'admin/settings',
  'admin/strings',
  'admin/processes',
  'admin/workflows',
  'admin/audit',
  'admin/connector',
];

const PLATFORM_ONLY_ROUTE_NAMES = [
  'admin-roles',
  'admin-workspaces',
  'admin-locales',
  'admin-domains',
  'admin-resources',
  'admin-system-logs',
  'admin-logs',
  'admin-settings',
  'admin-strings',
  'admin-processes',
  'admin-workflows',
  'admin-audit',
];

// Hub API endpoints that answer 403 to a tenant administrator in multitenant
// mode for every method. Roles, workspaces, locales, domains and strings are
// not listed: their reads stay available because forms use them as selectors.
const PLATFORM_ONLY_API_PREFIXES = [
  '/admin/config/settings',
  '/admin/import/settings',
  '/admin/connector',
  '/admin/instances',
  '/admin/resources',
  '/admin/audit',
  '/admin/processes',
  '/admin/workflows',
  '/admin/flows',
  '/tools/sendmail',
  '/tools/encrypt',
  '/tools/elastic',
  '/tools/logs',
  '/status/license',
];

function isFeature(value: unknown): value is AdministrationFeature {
  return typeof value === 'string' && (ADMINISTRATION_FEATURES as readonly string[]).includes(value);
}

/**
 * Reads `administration_disabled` the way the Hub API does. Only an explicit
 * list, such as `[]`, enables features. A missing, null or empty value means
 * the default, every feature disabled, so an older API without the key or a
 * settings load that failed never shows a page the API refuses. A value that
 * is not a list of known features, or repeats one, also disables every feature.
 */
export function normalizeAdministrationDisabled(value: unknown): AdministrationFeature[] {
  if (value === undefined || value === null || value === '') {
    return [...ADMINISTRATION_FEATURES];
  }

  let list = value;
  if (typeof value === 'string') {
    try {
      list = JSON.parse(value);
    } catch {
      return [...ADMINISTRATION_FEATURES];
    }
    if (list === null) {
      return [...ADMINISTRATION_FEATURES];
    }
  }

  if (!Array.isArray(list) || !list.every(isFeature) || new Set(list).size !== list.length) {
    return [...ADMINISTRATION_FEATURES];
  }

  return [...list];
}

export function normalizeMultitenantEnvironment(value: unknown): boolean {
  return value === true || value === 'true';
}

export function createAdministrationPolicy(disabledValue: unknown, multitenantValue: unknown, roles?: unknown): AdministrationPolicy {
  return {
    disabledFeatures: normalizeAdministrationDisabled(disabledValue),
    multitenant: normalizeMultitenantEnvironment(multitenantValue),
    platformAuthority: userHasDomainAdminRole({ role: roles as string[] | string | null | undefined }),
  };
}

function pathOf(link: string | undefined | null): string {
  if (!link) {
    return '';
  }

  try {
    return new URL(link, 'http://localhost').pathname;
  } catch {
    return String(link).split(/[?#]/)[0];
  }
}

/** True when `segments` (such as `admin/forms`) appear as whole consecutive path segments. */
function hasSegments(path: string, segments: string): boolean {
  const parts = path.split('/');
  const wanted = segments.split('/');
  return parts.some((_, start) => wanted.every((segment, offset) => parts[start + offset] === segment));
}

export function administrationFeatureOfAppPath(link: string | undefined | null): AdministrationFeature | null {
  const path = pathOf(link);
  return ADMINISTRATION_FEATURES.find((feature) => FEATURE_APP_PATHS[feature].some((segments) => hasSegments(path, segments))) ?? null;
}

/** The API path below `/api/v2`, with a leading slash. */
function apiPathOf(url: string | undefined | null): string {
  let path = pathOf(url);
  const prefixIndex = path.indexOf('/api/v2/');
  if (prefixIndex !== -1) {
    path = path.slice(prefixIndex + '/api/v2'.length);
  }
  return path.startsWith('/') ? path : `/${path}`;
}

function matchesApiPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/**
 * Classifies a Hub API request URL, with or without the `/api/v2` prefix.
 */
export function administrationFeatureOfApiPath(url: string | undefined | null): AdministrationFeature | null {
  const path = apiPathOf(url);
  return (
    ADMINISTRATION_FEATURES.find(
      (feature) =>
        FEATURE_API_PREFIXES[feature].some((prefix) => matchesApiPrefix(path, prefix)) ||
        (FEATURE_API_EXACT_PATHS[feature] ?? []).includes(path),
    ) ?? null
  );
}

function administrationFeatureOfRoute(route: RouteLike): AdministrationFeature | null {
  if (!route) {
    return null;
  }

  if (typeof route.name === 'string') {
    const name = route.name;
    const feature = ADMINISTRATION_FEATURES.find((candidate) => FEATURE_ROUTE_NAMES[candidate](name));
    if (feature) {
      return feature;
    }
  }

  return administrationFeatureOfAppPath(route.path);
}

function isMultitenantRemovedRoute(route: RouteLike): boolean {
  if (!route) {
    return false;
  }

  if (typeof route.name === 'string' && MULTITENANT_REMOVED_ROUTE_NAMES.includes(route.name)) {
    return true;
  }

  const path = pathOf(route.path);
  return MULTITENANT_REMOVED_PATHS.some((segments) => hasSegments(path, segments));
}

function isPlatformOnlyRoute(route: RouteLike): boolean {
  if (!route) {
    return false;
  }

  if (typeof route.name === 'string' && (PLATFORM_ONLY_ROUTE_NAMES.includes(route.name) || route.name.startsWith('admin-connector-'))) {
    return true;
  }

  const path = pathOf(route.path);
  return PLATFORM_ONLY_APP_PATHS.some((segments) => hasSegments(path, segments));
}

function unavailableReason(
  feature: AdministrationFeature | null,
  removedInMultitenantMode: boolean,
  platformOnly: boolean,
  policy: AdministrationPolicy,
): AdministrationUnavailableReason | null {
  if (feature && policy.disabledFeatures.includes(feature)) {
    return 'disabled';
  }
  if (removedInMultitenantMode && policy.multitenant) {
    return 'multitenant';
  }
  if (platformOnly && policy.multitenant && !policy.platformAuthority) {
    return 'platform';
  }
  return null;
}

/**
 * Why an admin page is unavailable, or `null` when it is available. A disabled
 * feature is reported first, then the multitenant removal, then a page that
 * only a platform administrator may use, as the API orders its checks. The
 * admin role check is separate: an available page still needs the admin role.
 */
export function adminRouteUnavailableReason(route: RouteLike, policy: AdministrationPolicy): AdministrationUnavailableReason | null {
  return unavailableReason(administrationFeatureOfRoute(route), isMultitenantRemovedRoute(route), isPlatformOnlyRoute(route), policy);
}

export function isAdminRouteAvailable(route: RouteLike, policy: AdministrationPolicy): boolean {
  return adminRouteUnavailableReason(route, policy) === null;
}

export function isAdminLinkAvailable(link: string | undefined | null, policy: AdministrationPolicy): boolean {
  return isAdminRouteAvailable({ path: link ?? '' }, policy);
}

/**
 * Whether the app may send a request to a Hub API URL. Unavailable endpoints
 * answer 403, so callers skip them instead of sending the request.
 */
export function isApiPathAvailable(url: string | undefined | null, policy: AdministrationPolicy): boolean {
  const path = apiPathOf(url);
  const removed = MULTITENANT_REMOVED_PATHS.some((segments) => matchesApiPrefix(path, `/${segments}`));
  const platformOnly = PLATFORM_ONLY_API_PREFIXES.some((prefix) => matchesApiPrefix(path, prefix));
  return unavailableReason(administrationFeatureOfApiPath(url), removed, platformOnly, policy) === null;
}

/** Whether a navigation target is one of the pages this policy can hide. */
export function isAdministrationPolicyRoute(route: RouteLike): boolean {
  return administrationFeatureOfRoute(route) !== null || isMultitenantRemovedRoute(route) || isPlatformOnlyRoute(route);
}
